import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, Subscriber } from 'rxjs';

import { TrialInsightsState } from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsQuery } from '@models/trial-insights/trial-insights.query';

import { getRemainingInvestigatorInfoQuery, SortOrder } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { GenericOperator } from '@shared/services/operator.service';
import { decimalDivide, decimalMultiply } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import {
  TIFPEChartType,
  TrialInsightsFinancePatientsEnrolledChartService,
} from './patients-enrolled-chart.service';
import { TrialInsightsFinancePatientsEnrolledTableService } from './patients-enrolled-table.service';
import {
  GenericTrialInsightsQuery,
  SortOrderDefault,
} from '../../classes/trial-insights-query.class';
import {
  TrialInsightPatientEnrolledKey,
  TrialInsightsTableRowData,
} from '../../models/trial-insights-table.model';

type Results = [State['data'], boolean];
type State = Partial<TrialInsightsState['patientEnrolled']>;

const sortDefaults: SortOrderDefault[] = [
  {
    buttonKey: TrialInsightPatientEnrolledKey.PATIENT_ENROLLED,
    defaultOrder: SortOrder.DESC,
  },
];

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsFinancePatientsEnrolledQueryService extends GenericTrialInsightsQuery<TIFPEChartType> {
  private launchDarklyService = inject(LaunchDarklyService);

  chartService: TrialInsightsFinancePatientsEnrolledChartService;
  tableService: TrialInsightsFinancePatientsEnrolledTableService;
  trialInsightsQuery: TrialInsightsQuery;

  private hundredPercent = 100;

  constructor() {
    const trialInsightsQuery = inject(TrialInsightsQuery);
    const tableService = inject(TrialInsightsFinancePatientsEnrolledTableService);
    const chartService = inject(TrialInsightsFinancePatientsEnrolledChartService);

    super({
      chartService,
      slice: 'patientEnrolled',
      sortDefaults,
      tableService,
      trialInsightsQuery,
    });

    this.trialInsightsQuery = trialInsightsQuery;
    this.tableService = tableService;
    this.chartService = chartService;
  }

  getRemainingInvestigatorInfo$() {
    return combineLatest([
      this.data as Observable<getRemainingInvestigatorInfoQuery[]>,
      this.launchDarklyService.select$((flags) => flags.tab_forecast_investigator_forecast),
    ]).pipe(this.processResponseData());
  }

  processResponseData() {
    const processFn = (sourceValue: Results, subscriber: Subscriber<Results>) => {
      const data = sourceValue[0];

      if (!data || !data.length) {
        this.resetState();

        return;
      }

      const totalAmount = data[0].amount_remaining || 0;

      const labels: [string, number][] = [];

      let xAxes = this.hundredPercent;

      const rowData: TrialInsightsTableRowData[] = data.map((remainingInvestigator) => {
        const patientsEnrolledPerc = decimalDivide(
          remainingInvestigator.patients_enrolled,
          remainingInvestigator.expected_patients_enrolled
        );

        labels.push([
          'Patients Enrolled',
          decimalMultiply(patientsEnrolledPerc, this.hundredPercent, 4),
        ]);

        const expectedEnrolledExceeded =
          remainingInvestigator.patients_enrolled >
          remainingInvestigator.expected_patients_enrolled;

        this.expectedEnrolledExceeded.next(expectedEnrolledExceeded);

        const isPatientEnrolledDataIsEmpty =
          remainingInvestigator.patients_enrolled === 0 &&
          remainingInvestigator.expected_patients_enrolled === 0;

        const leftSubheader = isPatientEnrolledDataIsEmpty
          ? Utils.zeroHyphen
          : `${remainingInvestigator.patients_enrolled} (${Utils.percentageFormatter(
              patientsEnrolledPerc,
              { minimumFractionDigits: 2 },
              true
            )})`;

        const patientsEnrolledPercSafeValue = isFinite(patientsEnrolledPerc)
          ? decimalMultiply(patientsEnrolledPerc, this.hundredPercent, 2)
          : 0;

        // To display chart tooltip in correct position when more then 100%
        xAxes =
          patientsEnrolledPercSafeValue > this.hundredPercent
            ? patientsEnrolledPercSafeValue
            : this.hundredPercent;

        return {
          buttonKey: TrialInsightPatientEnrolledKey.PATIENT_ENROLLED,
          leftHeader: 'Patients Enrolled',
          leftSubheader,
          rightHeader: '',
          rightSubheader: '',
        };
      });

      const datasets = this.chartService.createDatasets(labels);

      const chartOptions = this.chartService.createChart(datasets, xAxes);
      const tableOptions = this.tableService.createTable(rowData, sourceValue[1]);

      this.chartOptions.next(chartOptions);
      this.totalAmount.next(Utils.currencyFormatter(totalAmount));
      this.tableOptions.next(tableOptions);
      subscriber.next(sourceValue);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }
}
